import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, ChevronRight, MapPin, Plus, QrCode } from "lucide-react";

import {
  useContainers,
  useCreateLocation,
  useInventory,
  useLocationPath,
  useLocationTree,
} from "@/api/queries";
import type { LocationNode } from "@/api/types";
import { useCanWrite } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Photo } from "@/components/Photo";
import { useToast } from "@/components/toast-context";
import { LabelDialog } from "@/features/labels/LabelDialog";
import { PageHeader } from "@/layout/PageHeader";
import { Page } from "@/layout/Page";
import { cn } from "@/lib/cn";
import { formatContainerType, formatLocationType, formatQuantity, formatUnit } from "@/lib/format";

/** Generic types the product ships. A place only exists once someone creates it. */
const LOCATION_TYPES = [
  "WAREHOUSE",
  "ZONE",
  "AISLE",
  "RACK",
  "SHELF",
  "SLOT",
  "ROOM",
  "OFFICE",
  "WORKSHOP",
  "VEHICLE",
  "JOB_SITE",
  "CUSTOMER_SITE",
  "SITE",
  "YARD",
  "OTHER",
];

/**
 * Locations (TICKET FE-008).
 *
 * Tree on the left, contents on the right for desktop; breadcrumb plus a list of
 * children on mobile. The depth is not fixed, so nothing here assumes five
 * levels.
 */
export function LocationsPage() {
  const { locationId } = useParams();
  const { data: tree, isLoading, error, refetch } = useLocationTree();
  const [creating, setCreating] = useState(false);
  const canWrite = useCanWrite();

  return (
    <Page>
      <PageHeader
        title="Emplacements"
        subtitle="Entrepôts, zones, racks, tablettes, bacs — mais aussi camions et sites clients."
        actions={
          canWrite && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              Nouvel emplacement
            </Button>
          )
        }
      />

      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      <div className="grid min-w-0 gap-3 @4xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
        <Card
          className={cn(
            "flex min-w-0 flex-col overflow-hidden",
            locationId && "hidden @4xl:flex",
          )}
        >
          <CardHeader title="Structure" />
          {isLoading && <LoadingState />}
          {tree && tree.length === 0 && (
            <EmptyState
              icon={<MapPin className="size-6" aria-hidden />}
              title="Aucun emplacement"
              description="Créez un entrepôt, puis ses zones, racks et bacs."
            />
          )}
          {tree && tree.length > 0 && (
            <nav className="max-h-[70vh] overflow-y-auto p-2" aria-label="Arbre des emplacements">
              <LocationTree nodes={tree} selectedId={locationId} />
            </nav>
          )}
        </Card>

        {locationId ? (
          <LocationDetail locationId={locationId} />
        ) : (
          <Card className="hidden @4xl:block">
            <EmptyState
              icon={<MapPin className="size-6" aria-hidden />}
              title="Choisissez un emplacement"
              description="Le contenu, les contenants et le stock s'afficheront ici."
            />
          </Card>
        )}
      </div>

      <CreateLocationDialog
        open={creating}
        onOpenChange={setCreating}
        parentId={locationId ?? null}
        tree={tree ?? []}
      />
    </Page>
  );
}

function LocationTree({
  nodes,
  selectedId,
  depth = 0,
}: {
  nodes: LocationNode[];
  selectedId?: string;
  depth?: number;
}) {
  return (
    <ul className="flex flex-col">
      {nodes.map((node) => (
        <li key={node.id}>
          <Link
            to={`/locations/${node.id}`}
            style={{ paddingLeft: `${8 + depth * 14}px` }}
            className={cn(
              "flex items-center gap-1.5 rounded-md py-1.5 pr-2 text-[13px] transition-colors",
              node.id === selectedId
                ? "bg-brand-subtle font-medium text-brand"
                : "text-ink-muted hover:bg-surface-hover hover:text-ink",
            )}
          >
            {node.children.length > 0 ? (
              <ChevronRight className="size-3 shrink-0 text-ink-subtle" aria-hidden />
            ) : (
              <span className="size-3 shrink-0" />
            )}
            <span className="truncate">{node.name}</span>
            <span className="ml-auto shrink-0 font-mono text-[10px] text-ink-subtle">
              {node.code}
            </span>
          </Link>
          {node.children.length > 0 && (
            <LocationTree nodes={node.children} selectedId={selectedId} depth={depth + 1} />
          )}
        </li>
      ))}
    </ul>
  );
}

function LocationDetail({ locationId }: { locationId: string }) {
  const [labelOpen, setLabelOpen] = useState(false);
  const path = useLocationPath(locationId);
  const containers = useContainers({ location_id: locationId });
  const stock = useInventory({ location_id: locationId, in_stock_only: true, limit: 100 });

  const current = path.data?.[path.data.length - 1];

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Card>
        <CardBody>
          <nav aria-label="Chemin" className="flex flex-wrap items-center gap-1 text-[12px] text-ink-subtle">
            {(path.data ?? []).map((step, index) => (
              <span key={step.id} className="flex items-center gap-1">
                {index > 0 && <ChevronRight className="size-3" aria-hidden />}
                <Link to={`/locations/${step.id}`} className="hover:text-ink">
                  {step.name}
                </Link>
              </span>
            ))}
          </nav>
          <div className="mt-1 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-ink">{current?.name ?? "…"}</h2>
              <p className="font-mono text-[12px] text-ink-subtle">{current?.code}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setLabelOpen(true)}>
              <QrCode className="size-4" aria-hidden />
              Étiquette
            </Button>
          </div>
        </CardBody>
      </Card>

      <LabelDialog
        open={labelOpen}
        onOpenChange={setLabelOpen}
        entity="location"
        entityId={locationId}
      />

      <Card>
        <CardHeader
          title="Contenants ici"
          subtitle="Un contenant déplacé emmène tout son contenu."
        />
        {containers.isLoading && <LoadingState />}
        {containers.data && containers.data.length === 0 && (
          <EmptyState
            icon={<Box className="size-6" aria-hidden />}
            title="Aucun contenant"
            description="Packouts, bacs et coffres posés directement ici apparaîtront dans cette liste."
          />
        )}
        {containers.data && containers.data.length > 0 && (
          <ul className="divide-y divide-border/60">
            {containers.data.map((container) => (
              <li key={container.id}>
                <Link
                  to={`/containers/${container.id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover"
                >
                  <span className="flex size-9 items-center justify-center rounded-md border border-border bg-surface-sunken text-ink-subtle">
                    <Box className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] text-ink">
                      {container.name ?? container.container_code}
                    </span>
                    <span className="block truncate text-[11px] text-ink-subtle">
                      {container.container_code} · {formatContainerType(container.container_type)}
                    </span>
                  </span>
                  <ChevronRight className="size-4 text-ink-subtle" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Stock à cet emplacement"
          action={
            <Link
              to={`/inventory?location=${locationId}`}
              className="text-[12px] text-ink-muted hover:text-ink"
            >
              Ouvrir dans l'inventaire
            </Link>
          }
        />
        {stock.isLoading && <LoadingState />}
        {stock.data && stock.data.items.length === 0 && (
          <EmptyState title="Rien en stock ici" description="Aucun article n'est enregistré à cet emplacement." />
        )}
        {stock.data && stock.data.items.length > 0 && (
          <ul className="divide-y divide-border/60">
            {stock.data.items.map((row) => (
              <li key={row.product_id}>
                <Link
                  to={`/products/${row.product_id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover"
                >
                  <Photo mediaId={row.media_id} alt={row.name} className="size-10 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] text-ink">{row.name}</span>
                    <span className="block truncate text-[11px] text-ink-subtle">
                      {[row.manufacturer, row.part_number].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="tnum block text-[15px] font-semibold text-ink">
                      {formatQuantity(row.available_quantity)}
                    </span>
                    <span className="block text-[10px] text-ink-subtle">
                      {formatUnit(row.unit)} dispo.
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function CreateLocationDialog({
  open,
  onOpenChange,
  parentId,
  tree,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId: string | null;
  tree: LocationNode[];
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("SLOT");
  const [parent, setParent] = useState(parentId ?? "");
  const [error, setError] = useState<string | null>(null);

  const flat = useMemo(() => flatten(tree), [tree]);
  const mutation = useCreateLocation({
    onSuccess: (location) => {
      toast.success("Emplacement créé", `Code attribué : ${location.code}`);
      onOpenChange(false);
      setName("");
      setDescription("");
    },
    onError: (cause) => setError(cause.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setError(null);
        onOpenChange(next);
      }}
      title="Nouvel emplacement"
      description="Vous donnez le type et le nom. HD-Vault attribue le code d'étiquette."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate({
                name: name.trim(),
                description: description.trim() || undefined,
                location_type: type,
                parent_id: parent || null,
              });
            }}
            disabled={!name.trim()}
          >
            Créer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Type" htmlFor="location-type">
          <Select id="location-type" value={type} onChange={(event) => setType(event.target.value)}>
            {LOCATION_TYPES.map((value) => (
              <option key={value} value={value}>
                {formatLocationType(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Nom"
          htmlFor="location-name"
          hint="Le vôtre. Le code d'étiquette est attribué automatiquement."
        >
          <Input
            id="location-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            placeholder="Ex. : Connecteurs EMT"
          />
        </Field>
        <Field label="Emplacement parent" htmlFor="location-parent" hint="Vide = à la racine">
          <Select
            id="location-parent"
            value={parent}
            onChange={(event) => setParent(event.target.value)}
          >
            <option value="">— Racine —</option>
            {flat.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {"— ".repeat(entry.depth)}
                {entry.name} ({entry.code})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Description" htmlFor="location-description" hint="Facultatif">
          <Input
            id="location-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        {error && (
          <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-[13px] text-danger">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function flatten(nodes: LocationNode[], depth = 0): { id: string; code: string; name: string; depth: number }[] {
  return nodes.flatMap((node) => [
    { id: node.id, code: node.code, name: node.name, depth },
    ...flatten(node.children, depth + 1),
  ]);
}
